import { FC, Fragment, useReducer, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  IconButton,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit, Delete, Add, AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import { AwardTableCell, AwardLeftStickyTableCell, AwardRightStickyTableCell } from './AwardTableCell';
import { CriteriaListItem, CriteriaTypeEnum } from 'src/app/modules/SimulationTool/types/criteria-setting';
import { awardFormReducer, AwardFormState } from '../reducers/award-form-reducer';
import { CriteriaOperatorEnum } from 'src/app/modules/SimulationTool/types/parameter-setting';
import { numberSeparator } from 'src/app/common/utils';

type AwardSectionProps = {
  isDisabled: boolean;
  criteriaList: CriteriaListItem[];
  initialFormState: AwardFormState;
  hasError: boolean;
  paremeterType: string;
  onChange: (newAwards: AwardFormState['awards']) => void;
  onDismissErrorHandler: (field: string, value: any) => void;
};

const CRITERIA_LIMIT = 7;
const CONDITION_LIMIT = 10;

const CRITERIA_COLUMN_WIDTH = 160;

const useStyles = makeStyles()((theme) => ({
  criteriaCell: {
    width: CRITERIA_COLUMN_WIDTH,
    minWidth: CRITERIA_COLUMN_WIDTH,
  },
  iconButton: {
    color: 'black',
    padding: 5,
  },
  noMargin: {
    margin: 0,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rowSpacing: {
    marginBottom: 16,
  },
  tier: {
    minWidth: 60,
  },
  tierTextField: {
    width: 150,
    marginRight: 10,
  },
  select: {
    minWidth: 120,
  },
  checkbox: {
    paddingLeft: 0,
  },
  criteriaTextField: {
    width: 120,
  },
  orText: {
    color: '#919EAB',
    fontWeight: 400,
    marginRight: 10,
  },
  andText: {
    color: '#919EAB',
    fontWeight: 400,
    margin: '0 12px 0 12px',
  },
  criteriaOperationCell: {
    width: 87,
    minWidth: 87,
  },
  criteriaOperationIconButton: {
    color: '#58647A',
    padding: 0,
  },
  criteriaOperationIcon: {
    width: 25,
    height: 25,
  },
  smallSpacing: {
    marginRight: 5,
  },
  spacing: {
    marginRight: 10,
  },
  errorText: {
    fontSize: 10,
    fontWeight: 400,
    fontFamily: 'Poppins',
    color: '#F018A6',
    marginTop: 4,
  },
}));

const AwardSection: FC<AwardSectionProps> = ({
  isDisabled,
  criteriaList,
  initialFormState,
  hasError,
  onChange,
  onDismissErrorHandler,
  paremeterType,
}) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);

  const [formState, formDispatch] = useReducer(awardFormReducer, initialFormState);
  const { awards, editableTiers } = formState;

  useEffect(() => {
    onChange(awards);
  }, [awards]);

  return (
    <>
      <div>{hasError && <div className={classes.errorText}>All fields in Condition are mandatory</div>}</div>
      {awards.map((award, awardIndex) => {
        const disableAddCriteria = isDisabled || award.requirements.length === CRITERIA_LIMIT;
        return (
          <div key={`${award.name}-${awardIndex}`}>
            <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
              <div className={classes.rowContainer}>
                <span className={`${classes.tier} ${classes.spacing}`}>{`${award.tierCode} - ${award.name}`}</span>
              </div>
            </div>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <AwardLeftStickyTableCell className={classes.criteriaCell}>
                      <div className={classes.rowContainer}>
                        <div className={classes.rowContainer}>
                          <span className={classes.smallSpacing}>{Translation('parameterSetting.criteria')}</span>
                        </div>
                        {/* <div style={{ flexGrow: 1, textAlign: 'right' }}>
                          <IconButton
                            disabled={disableAddCriteria}
                            className={classes.iconButton}
                            style={{
                              color: 'white',
                              visibility: disableAddCriteria ? 'hidden' : 'visible',
                            }}
                            onClick={() => formDispatch({ type: 'ADD_CRITERIA', payload: { awardIndex } })}
                          >
                            <Add />
                          </IconButton>
                        </div> */}
                      </div>
                    </AwardLeftStickyTableCell>
                    <AwardTableCell>{Translation('parameterSetting.condition')}</AwardTableCell>
                    <AwardRightStickyTableCell className={classes.criteriaOperationCell} />
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
            <TableContainer className={classes.rowSpacing}>
              <Table>
                <TableBody>
                  {award.requirements.map((requirement, requirementIndex) => (
                    <TableRow key={`${requirement.name}-${requirementIndex}`}>
                      <AwardLeftStickyTableCell className={classes.criteriaCell}>
                        <div className={classes.rowContainer}>
                          <span
                            className={classes.orText}
                            style={{ visibility: requirementIndex === 0 ? 'hidden' : 'visible' }}
                          >
                            OR
                          </span>
                          {/* <IconButton
                            disabled={isDisabled}
                            className={classes.iconButton}
                            onClick={() => {
                              formDispatch({ type: 'REMOVE_CRITERIA', payload: { awardIndex, requirementIndex } });
                              if (hasError) {
                                onDismissErrorHandler('awards', true);
                              }
                            }}
                          >
                            <Delete />
                          </IconButton> */}
                          <span>{`${Translation('parameterSetting.criteria')} ${requirementIndex + 1}`}</span>
                        </div>
                      </AwardLeftStickyTableCell>
                      <AwardTableCell>
                        <div className={classes.rowContainer}>
                          {requirement.items.map((item, itemIndex) => {
                            const itemsLength = requirement.items.length;
                            return (
                              <Fragment key={`item-${itemIndex}`}>
                                <div className={classes.rowContainer}>
                                  <FormControl
                                    disabled={isDisabled}
                                    className={`${classes.select} ${classes.noMargin}`}
                                    margin="dense"
                                    variant="outlined"
                                  >
                                    <Select
                                      value={item.keyId}
                                      onChange={(e) => {
                                        const newItem = { ...item, keyId: e.target.value as string };
                                        newItem.operator = CriteriaOperatorEnum.GREATER_THAN_EQUAL;
                                        newItem.value = '';

                                        formDispatch({
                                          type: 'MODIFY_ITEM',
                                          payload: {
                                            awardIndex,
                                            requirementIndex,
                                            itemIndex,
                                            value: newItem,
                                          },
                                        });
                                        if (hasError) {
                                          onDismissErrorHandler('awards', e.target.value);
                                        }
                                      }}
                                    >
                                      {criteriaList.map((kpiItem) => (
                                        <MenuItem key={kpiItem._id} value={kpiItem._id}>
                                          {kpiItem.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <FormControl
                                    disabled={true}
                                    className={classes.noMargin}
                                    margin="dense"
                                    variant="outlined"
                                  >
                                    <Select
                                      value={item.operator}
                                      onChange={(e) =>
                                        formDispatch({
                                          type: 'MODIFY_ITEM',
                                          payload: {
                                            awardIndex,
                                            requirementIndex,
                                            itemIndex,
                                            value: { ...item, operator: e.target.value as CriteriaOperatorEnum },
                                          },
                                        })
                                      }
                                    >
                                      {Object.values(CriteriaOperatorEnum).map((operator) => (
                                        <MenuItem key={operator} value={operator}>
                                          {operator}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <TextField
                                    disabled={isDisabled}
                                    className={`${classes.criteriaTextField} ${classes.noMargin}`}
                                    margin="dense"
                                    variant="outlined"
                                    placeholder={Translation('parameterSetting.input_placeholder')}
                                    value={item.value}
                                    onChange={(e) => {
                                      let value = e.target.value;

                                      if (paremeterType === CriteriaTypeEnum.NUMBER) {
                                        const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                                        value = numberSeparator(onlyNums);
                                      }

                                      if (paremeterType === CriteriaTypeEnum.PERCENTAGE) {
                                        if (!/^\d*\.?\d*$/.test(value)) return;

                                        const [integerPart, decimalPart] = value.split('.');
                                        if (integerPart.length > 3) return;

                                        const intVal = parseInt(integerPart || '0', 10);
                                        if (intVal > 100) return;

                                        if (decimalPart && decimalPart.length > 2) return;
                                      }

                                      formDispatch({
                                        type: 'MODIFY_ITEM',
                                        payload: {
                                          awardIndex,
                                          requirementIndex,
                                          itemIndex,
                                          value: { ...item, value },
                                        },
                                      });
                                      if (hasError) {
                                        onDismissErrorHandler('awards', e.target.value);
                                      }
                                    }}
                                  />
                                </div>
                                {itemsLength > 1 && itemIndex !== itemsLength - 1 && (
                                  <span className={classes.andText}>AND</span>
                                )}
                              </Fragment>
                            );
                          })}
                        </div>
                      </AwardTableCell>
                      {/* <AwardRightStickyTableCell className={classes.criteriaOperationCell}>
                        <div className={classes.rowContainer}>
                          <IconButton
                            disabled={isDisabled || requirement.items.length === 1}
                            className={`${classes.criteriaOperationIconButton} ${classes.smallSpacing}`}
                            onClick={() => {
                              formDispatch({
                                type: 'REMOVE_ITEM',
                                payload: { awardIndex, requirementIndex, itemIndex: requirement.items.length - 1 },
                              });
                              if (hasError) {
                                onDismissErrorHandler('awards', true);
                              }
                            }}
                          >
                            <RemoveCircleOutline className={classes.criteriaOperationIcon} />
                          </IconButton>
                          <IconButton
                            disabled={isDisabled || requirement.items.length === CONDITION_LIMIT}
                            className={classes.criteriaOperationIconButton}
                            onClick={() =>
                              formDispatch({ type: 'ADD_ITEM', payload: { awardIndex, requirementIndex } })
                            }
                          >
                            <AddCircleOutline className={classes.criteriaOperationIcon} />
                          </IconButton>
                        </div>
                      </AwardRightStickyTableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        );
      })}
    </>
  );
};

export default AwardSection;
